// CRITICAL: this directive MUST stay at the very top of the file.
// app.json has `experiments.reactCompiler: true` (React 19), so the React
// Compiler memoizes every component in the project by default. But
// react-native-android-widget does NOT render these components through React —
// its reconciler (build-widget-tree.js) walks the tree and calls the component
// as a RAW function: `jsxTree = jsxTree.type(jsxTree.props)`. A compiler-
// memoized component calls `useMemo`/`_c()` internally, so that raw call throws
// "Invalid hook call", buildWidgetTree rethrows, and the ENTIRE tile renders
// BLANK. "use no memo" opts this whole file out of the compiler so every widget
// component stays a plain function the reconciler can call directly.
'use no memo';

import React from 'react';
import {
  FlexWidget,
  ImageWidget,
  TextWidget,
  type ImageWidgetSource,
} from 'react-native-android-widget';

import type { WFixture, WidgetData, WMatch, WStanding, WTeam } from './matchData';

/**
 * The four selectable widget looks (Glassify-style style picker). The SAME
 * match-card layout renders for all four; only the color/token palette `c`
 * changes per style, so there is zero structural divergence and no new path to
 * the blank-tile bug. `gradient` is the default (matches the previous shipped
 * look). The persisted preference lives in AsyncStorage (see ./widgetStyle).
 */
export type WidgetStyle = 'dark' | 'gradient' | 'glass' | 'minimal';
export const WIDGET_STYLES: readonly WidgetStyle[] = ['dark', 'gradient', 'glass', 'minimal'];
export const DEFAULT_WIDGET_STYLE: WidgetStyle = 'gradient';

/** Coerce any persisted/unknown value to a valid style (robust → default). */
export function normalizeWidgetStyle(v: unknown): WidgetStyle {
  return typeof v === 'string' && (WIDGET_STYLES as readonly string[]).includes(v)
    ? (v as WidgetStyle)
    : DEFAULT_WIDGET_STYLE;
}

/**
 * Accent color choice — recolors the brand-edge / live / highlight system on top
 * of whichever base `style` is picked (the gradient PLATE stays per-style; only
 * the cyan-family tokens are overridden). `cyan` is the default and reproduces
 * the original look exactly. Each accent ships a bright + base hex; the alpha
 * variants used by chips/borders are derived from `base` at render time.
 */
export type WidgetAccent = 'cyan' | 'violet' | 'emerald' | 'amber' | 'rose';
export const WIDGET_ACCENTS: readonly WidgetAccent[] = ['cyan', 'violet', 'emerald', 'amber', 'rose'];
export const DEFAULT_WIDGET_ACCENT: WidgetAccent = 'cyan';

export function normalizeWidgetAccent(v: unknown): WidgetAccent {
  return typeof v === 'string' && (WIDGET_ACCENTS as readonly string[]).includes(v)
    ? (v as WidgetAccent)
    : DEFAULT_WIDGET_ACCENT;
}

/**
 * Widget LAYOUT (which content the tile leads with):
 *  - `auto`     — the original adaptive behavior: hero match card, then group
 *                 standings (live/ft) or the upcoming list (tall, pre).
 *  - `upcoming` — a Glassify-style multi-row "Upcoming fixtures" board: the next
 *                 N fixtures as [flag/code · time+date · flag/code] rows on the
 *                 glass plate, with no single hero card. Falls back to `auto`'s
 *                 focus card / empty state when there are no upcoming fixtures.
 */
export type WidgetLayout = 'auto' | 'upcoming';
export const WIDGET_LAYOUTS: readonly WidgetLayout[] = ['auto', 'upcoming'];
export const DEFAULT_WIDGET_LAYOUT: WidgetLayout = 'auto';

export function normalizeWidgetLayout(v: unknown): WidgetLayout {
  return typeof v === 'string' && (WIDGET_LAYOUTS as readonly string[]).includes(v)
    ? (v as WidgetLayout)
    : DEFAULT_WIDGET_LAYOUT;
}

/**
 * The full persisted widget preference bundle. Stored as one JSON blob (see
 * ./widgetStyle). Every field has a robust normalizer so a missing/partial/junk
 * value collapses to its default — the headless task can always render.
 */
export interface WidgetPrefs {
  style: WidgetStyle;
  accent: WidgetAccent;
  layout: WidgetLayout;
  /** Float a followed team's fixture to the top + highlight its crest. */
  favoriteFirst: boolean;
}

export const DEFAULT_WIDGET_PREFS: WidgetPrefs = {
  style: DEFAULT_WIDGET_STYLE,
  accent: DEFAULT_WIDGET_ACCENT,
  layout: DEFAULT_WIDGET_LAYOUT,
  favoriteFirst: false,
};

/** Coerce any object/unknown into a complete, valid WidgetPrefs (never throws). */
export function normalizeWidgetPrefs(v: unknown): WidgetPrefs {
  const o = (v ?? {}) as Record<string, unknown>;
  return {
    style: normalizeWidgetStyle(o.style),
    accent: normalizeWidgetAccent(o.accent),
    layout: normalizeWidgetLayout(o.layout),
    favoriteFirst: o.favoriteFirst === true,
  };
}

/**
 * Design tokens from the widget handoff (8-digit hex carries alpha).
 *
 * The root surface is built from a top->bottom GRADIENT (gradStart -> gradEnd)
 * for an iOS-style glassy plate. Every gradient node here passes its border
 * radius through `rmap()` (see its doc block for the exact native contract in
 * v0.20.3 — numeric radius is the safe form, an object map crashes).
 *
 * Per style, the OPACITY contract differs:
 *  - dark / gradient / minimal: the root plate stops are FULLY OPAQUE — a
 *    home-screen widget paints over the wallpaper, and translucency on an
 *    opaque-intent style reads as washed-out / "blank".
 *  - glass: the root plate + panels DELIBERATELY use 8-digit alpha hex so the
 *    wallpaper shows through for a true glass feel (the chips/borders stay
 *    bright enough that the content never reads as blank).
 *
 * Depth is faked the way glass UIs do it: a darker base gradient, a slightly
 * lighter inset "card" panel layered on top, hairline white top-highlights, and
 * a cyan accent system for the brand edge + live state.
 */
// Every value is an 8-/6-digit hex literal, so the field type is the library's
// HexColor (`#${string}`) — NOT plain `string`. react-native-android-widget's
// color props are typed `ColorProp = HexColor | RgbaColor`; a widened `string`
// would fail to assign. Keeping the field type as `#${string}` lets the theme
// objects flow straight into FlexWidget/TextWidget style props.
type HexColor = `#${string}`;
type WidgetTheme = {
  gradStart: HexColor;
  gradEnd: HexColor;
  panelStart: HexColor;
  panelEnd: HexColor;
  surface: HexColor;
  edge: HexColor;
  highlight: HexColor;
  ink: HexColor;
  soft: HexColor;
  muted: HexColor;
  inactive: HexColor;
  cyanBright: HexColor;
  cyanGlass: HexColor;
  cyanLine: HexColor;
  cyanSoft: HexColor;
  accentTop: HexColor;
  accentBot: HexColor;
  favFill: HexColor;
  favBorder: HexColor;
  rowLine: HexColor;
  chip: HexColor;
  chipLine: HexColor;
  ftChip: HexColor;
  ftChipLine: HexColor;
  scoreChipStart: HexColor;
  scoreChipEnd: HexColor;
};

const THEMES: Record<WidgetStyle, WidgetTheme> = {
  // GRADIENT — the glassy gradient plate (deep indigo -> near-black). This is
  // the previously-shipped premium look and stays the default.
  gradient: {
    gradStart: '#1C1D3A',
    gradEnd: '#0E0F22',
    panelStart: '#26284C',
    panelEnd: '#191A35',
    surface: '#15162E',
    edge: '#22D3FF52',
    highlight: '#FFFFFF1A',
    ink: '#F4F5FB',
    soft: '#C9CCEA',
    muted: '#9698BE',
    inactive: '#6B6E94',
    cyanBright: '#5FE9FF',
    cyanGlass: '#22D3FF24',
    cyanLine: '#22D3FF5C',
    cyanSoft: '#22D3FF14',
    accentTop: '#5FE9FF',
    accentBot: '#22D3FF',
    favFill: '#22D3FF1A',
    favBorder: '#22D3FF38',
    rowLine: '#FFFFFF12',
    chip: '#FFFFFF12',
    chipLine: '#FFFFFF24',
    ftChip: '#FFFFFF0D',
    ftChipLine: '#FFFFFF1F',
    scoreChipStart: '#2A2C52',
    scoreChipEnd: '#15162E',
  },
  // DARK — the current solid premium dark card. Flat near-black plate (the
  // gradient stops collapse to the same opaque ink) with the cyan accent kept.
  dark: {
    gradStart: '#16172C',
    gradEnd: '#16172C',
    panelStart: '#1E1F3A',
    panelEnd: '#1E1F3A',
    surface: '#16172C',
    edge: '#22D3FF3D',
    highlight: '#FFFFFF14',
    ink: '#F4F5FB',
    soft: '#C9CCEA',
    muted: '#9698BE',
    inactive: '#6B6E94',
    cyanBright: '#5FE9FF',
    cyanGlass: '#22D3FF20',
    cyanLine: '#22D3FF4F',
    cyanSoft: '#22D3FF12',
    accentTop: '#5FE9FF',
    accentBot: '#22D3FF',
    favFill: '#22D3FF1A',
    favBorder: '#22D3FF38',
    rowLine: '#FFFFFF10',
    chip: '#FFFFFF10',
    chipLine: '#FFFFFF1F',
    ftChip: '#FFFFFF0A',
    ftChipLine: '#FFFFFF1A',
    scoreChipStart: '#23244A',
    scoreChipEnd: '#16172C',
  },
  // GLASS — semi-transparent surfaces (8-digit alpha hex, ~CC/0.80 plate, ~B3
  // panels) so the wallpaper shows through for a true frosted-glass feel. The
  // accent edges stay bright so content never reads as blank.
  glass: {
    gradStart: '#1C1D3ACC',
    gradEnd: '#0E0F22B3',
    panelStart: '#2A2C52A6',
    panelEnd: '#191A3599',
    surface: '#15162ECC',
    edge: '#22D3FF66',
    highlight: '#FFFFFF26',
    ink: '#F8F9FE',
    soft: '#D6D9F2',
    muted: '#AFB1D6',
    inactive: '#8082AC',
    cyanBright: '#7DEEFF',
    cyanGlass: '#22D3FF33',
    cyanLine: '#22D3FF70',
    cyanSoft: '#22D3FF1F',
    accentTop: '#7DEEFF',
    accentBot: '#22D3FF',
    favFill: '#22D3FF26',
    favBorder: '#22D3FF4D',
    rowLine: '#FFFFFF1A',
    chip: '#FFFFFF1F',
    chipLine: '#FFFFFF33',
    ftChip: '#FFFFFF14',
    ftChipLine: '#FFFFFF26',
    scoreChipStart: '#2A2C52B3',
    scoreChipEnd: '#15162EB3',
  },
  // MINIMAL — clean, lighter, restrained. A soft slate plate, near-flat panels,
  // muted neutral accents (cyan dialled right back) for a calm, modern look.
  minimal: {
    gradStart: '#222338',
    gradEnd: '#1B1C2E',
    panelStart: '#2A2B42',
    panelEnd: '#26273C',
    surface: '#222338',
    edge: '#FFFFFF1F',
    highlight: '#FFFFFF14',
    ink: '#F2F3F9',
    soft: '#BFC2DA',
    muted: '#9092B0',
    inactive: '#6E708E',
    cyanBright: '#A9D9E6',
    cyanGlass: '#FFFFFF10',
    cyanLine: '#FFFFFF26',
    cyanSoft: '#FFFFFF0D',
    accentTop: '#C6CBE6',
    accentBot: '#9DA2C4',
    favFill: '#FFFFFF12',
    favBorder: '#FFFFFF2B',
    rowLine: '#FFFFFF0D',
    chip: '#FFFFFF0D',
    chipLine: '#FFFFFF1A',
    ftChip: '#FFFFFF08',
    ftChipLine: '#FFFFFF14',
    scoreChipStart: '#2E2F48',
    scoreChipEnd: '#222338',
  },
};

/**
 * Accent palette. Each entry is a base + bright 6-digit hex; every alpha-bearing
 * accent token in a theme is rebuilt by appending a fixed 8-digit alpha suffix to
 * `base` (the SAME suffixes the original cyan theme used per role, so glass stays
 * glassy and the contrast budget is preserved). `cyan` reproduces the shipped
 * look bit-for-bit. Picking an accent recolors the brand edge / live pill /
 * highlight / followed-row wash; the gradient PLATE stays per-style.
 */
type AccentSpec = { base: string; bright: string };
const ACCENTS: Record<WidgetAccent, AccentSpec> = {
  cyan: { base: '22D3FF', bright: '#5FE9FF' },
  violet: { base: '8B7BFF', bright: '#B9AEFF' },
  emerald: { base: '34E0A1', bright: '#6FF3C4' },
  amber: { base: 'FFB638', bright: '#FFD27A' },
  rose: { base: 'FF6FA5', bright: '#FF9EC2' },
};

/**
 * Overlay the chosen accent onto a base theme. Only the cyan-family / brand
 * tokens are recolored; the plate, panels, ink and neutrals are left untouched
 * so each base STYLE keeps its identity (a violet accent on the glass style is
 * still a frosted plate, just with violet edges). Returns a fresh WidgetTheme;
 * the `cyan` accent returns the base unchanged. Robust: an unknown accent
 * normalizes to cyan upstream, so `a` is always defined.
 */
function applyAccent(base: WidgetTheme, accent: WidgetAccent): WidgetTheme {
  if (accent === 'cyan') return base;
  const a = ACCENTS[accent];
  const hex = (alpha: string): HexColor => `#${a.base}${alpha}`;
  const bright = a.bright as HexColor;
  return {
    ...base,
    edge: hex('52'),
    cyanBright: bright,
    cyanGlass: hex('24'),
    cyanLine: hex('5C'),
    cyanSoft: hex('14'),
    accentTop: bright,
    accentBot: `#${a.base}` as HexColor,
    favFill: hex('1A'),
    favBorder: hex('38'),
  };
}

/**
 * borderRadius for a node that ALSO has a `backgroundGradient`.
 *
 * IMPORTANT — verified against the INSTALLED react-native-android-widget@0.20.3
 * source (not folklore): the native `BaseWidget.java` gradient branch does
 * `props.getMap("borderRadius").getDouble("topLeft")` — it needs `borderRadius`
 * to reach native as a flat `{topLeft,topRight,bottomLeft,bottomRight}` map. The
 * JS layer's `convertCommonStyle` (style.utils.js) is what builds that map, and
 * it ONLY does so from a NUMERIC `borderRadius` (it spreads `props.borderRadius`
 * into all four corners). Passing an OBJECT `{topLeft,...}` here produces a
 * NESTED `{topLeft:{topLeft:…}}` → native `getDouble` on a map → CRASH.
 *
 * Empirically confirmed: numeric -> correct flat map (safe with gradient);
 * object/map input -> nested map (the actual crash). So this helper returns a
 * plain number; the only safe gradient radius in THIS version is numeric. (The
 * older "use the map form" note pre-dates the JS normalization in 0.20.3.)
 */
const rmap = (r: number): number => r;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
// 12-hour clock with AM/PM (the whole app shows 12h, never 24h).
const fmtTime = (iso: string) => {
  const d = new Date(iso);
  const h = d.getHours();
  return `${h % 12 || 12}:${pad(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'}`;
};
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return `${pad(d.getDate())} ${MON[d.getMonth()] ?? ''}`;
};

/**
 * react-native-android-widget only accepts a remote image whose uri starts with
 * `http:`, `https:`, or `data:image`. Anything else (empty string, a
 * protocol-relative `//cdn/...`, a bare path) yields an unloadable source and an
 * empty box. Validate first; otherwise fall back to the always-renderable
 * team-code chip so the slot is never blank.
 */
function isUsableLogo(url: string | null): url is string {
  return (
    typeof url === 'string' &&
    (url.startsWith('https:') || url.startsWith('http:') || url.startsWith('data:image'))
  );
}

function Flag({ team, size, c }: { team: WTeam; size: number; c: WidgetTheme }) {
  if (isUsableLogo(team.logo)) {
    return (
      <ImageWidget
        image={team.logo as ImageWidgetSource}
        imageWidth={Math.round(size * 1.5)}
        imageHeight={size}
        radius={5}
      />
    );
  }
  // No remote crest: render an always-visible glassy code chip so the slot is
  // never blank. Solid bg + hairline border keeps it on-brand with the plate.
  return (
    <FlexWidget
      style={{
        width: Math.round(size * 1.5),
        height: size,
        backgroundColor: c.chip,
        borderWidth: 1,
        borderColor: c.chipLine,
        borderRadius: 5,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <TextWidget
        text={team.code.slice(0, 3)}
        style={{ fontSize: Math.max(8, Math.round(size * 0.42)), color: c.soft, fontWeight: '700', letterSpacing: 0.5 }}
      />
    </FlexWidget>
  );
}

/**
 * A crest badge: the flag/code chip wrapped in a subtle glassy disc so logos of
 * any shape read crisply on the plate (the "iPhone widget" look). Used in the
 * hero match row where the crests should feel premium and tactile.
 */
function Crest({ team, size, c }: { team: WTeam; size: number; c: WidgetTheme }) {
  const box = Math.round(size * 1.5) + 16;
  return (
    <FlexWidget
      style={{
        width: box,
        height: size + 14,
        backgroundColor: c.cyanSoft,
        borderWidth: 1,
        borderColor: c.chipLine,
        borderRadius: rmap(14),
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Flag team={team} size={size} c={c} />
    </FlexWidget>
  );
}

function Header({ match, c }: { match: WMatch; c: WidgetTheme }) {
  const right = match.status === 'ft' ? 'FULL TIME' : match.status === 'pre' ? fmtDate(match.kickoffAt) : null;
  return (
    <FlexWidget
      style={{ width: 'match_parent', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
        {/* Slim vertical accent strip (gradient -> brand glow) */}
        <FlexWidget
          style={{
            width: 4,
            height: 16,
            borderRadius: rmap(2),
            backgroundGradient: { from: c.accentTop, to: c.accentBot, orientation: 'TOP_BOTTOM' },
            marginRight: 9,
          }}
        />
        <TextWidget
          text={(match.competition + (match.round ? ` · ${match.round}` : '')).slice(0, 28)}
          style={{ fontSize: 11, color: c.soft, fontWeight: '700', letterSpacing: 0.8 }}
          truncate="END"
          maxLines={1}
        />
      </FlexWidget>
      {right ? (
        <FlexWidget
          style={{
            backgroundColor: c.chip,
            borderWidth: 1,
            borderColor: c.chipLine,
            borderRadius: rmap(8),
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          <TextWidget text={right} style={{ fontSize: 9, color: c.muted, fontWeight: '700', letterSpacing: 0.8 }} />
        </FlexWidget>
      ) : null}
    </FlexWidget>
  );
}

/** LIVE pill + minute, kickoff time, or the FT chip — each with the scorer line. */
function StatusLine({ match, c }: { match: WMatch; c: WidgetTheme }) {
  const scorer = match.scorer?.name
    ? `${match.scorer.name}${match.scorer.minute != null ? ` ${match.scorer.minute}'` : ''}`
    : null;

  if (match.status === 'live') {
    return (
      <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
        <FlexWidget
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundGradient: { from: c.cyanGlass, to: c.cyanSoft, orientation: 'TOP_BOTTOM' },
            borderWidth: 1,
            borderColor: c.cyanLine,
            borderRadius: rmap(9),
            paddingTop: 3,
            paddingBottom: 3,
            paddingLeft: 8,
            paddingRight: 9,
          }}
        >
          <FlexWidget style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.cyanBright, marginRight: 6 }} />
          <TextWidget text="LIVE" style={{ fontSize: 10, color: c.cyanBright, fontWeight: '700', letterSpacing: 1.2 }} />
          {match.minute != null ? (
            <TextWidget
              text={`  ${match.minute}'`}
              style={{ fontSize: 10, color: c.cyanBright, fontWeight: '700' }}
            />
          ) : null}
        </FlexWidget>
        {scorer ? (
          <TextWidget
            text={`   ${scorer}`}
            style={{ fontSize: 10, color: c.soft, fontWeight: '600' }}
            truncate="END"
            maxLines={1}
          />
        ) : null}
      </FlexWidget>
    );
  }

  if (match.status === 'pre') {
    return (
      <FlexWidget
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: c.chip,
          borderWidth: 1,
          borderColor: c.chipLine,
          borderRadius: rmap(9),
          paddingTop: 3,
          paddingBottom: 3,
          paddingLeft: 10,
          paddingRight: 11,
        }}
      >
        <FlexWidget style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: c.muted, marginRight: 6 }} />
        <TextWidget text="KICKOFF" style={{ fontSize: 9, color: c.inactive, fontWeight: '700', letterSpacing: 1.2 }} />
        <TextWidget
          text={`  ${fmtTime(match.kickoffAt)}`}
          style={{ fontSize: 11, color: c.soft, fontWeight: '700' }}
        />
      </FlexWidget>
    );
  }

  // Full-time: FT chip + scorer
  return (
    <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
      <FlexWidget
        style={{
          backgroundColor: c.ftChip,
          borderWidth: 1,
          borderColor: c.ftChipLine,
          borderRadius: rmap(9),
          paddingTop: 3,
          paddingBottom: 3,
          paddingLeft: 9,
          paddingRight: 9,
        }}
      >
        <TextWidget text="FULL TIME" style={{ fontSize: 9, color: c.muted, fontWeight: '700', letterSpacing: 1.2 }} />
      </FlexWidget>
      {scorer ? (
        <TextWidget
          text={`  ${scorer}`}
          style={{ fontSize: 11, color: c.muted, fontWeight: '600' }}
          truncate="END"
          maxLines={1}
        />
      ) : null}
    </FlexWidget>
  );
}

function score(match: WMatch): string {
  if (match.status === 'pre') return fmtTime(match.kickoffAt);
  return `${match.homeScore ?? 0} - ${match.awayScore ?? 0}`;
}

function MatchRow({ match, big, c }: { match: WMatch; big: boolean; c: WidgetTheme }) {
  const flag = big ? 24 : 18;
  const codeSize = big ? 15 : 12;
  const pre = match.status === 'pre';
  // Pre-match: the center shows the kickoff time small (the StatusLine already
  // carries it); live/ft: a bold score with strong hierarchy.
  const scoreSize = pre ? (big ? 17 : 14) : big ? 34 : 26;
  const scoreColor = pre ? c.soft : c.ink;
  return (
    <FlexWidget style={{ width: 'match_parent', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
      <FlexWidget style={{ flex: 1, flexDirection: 'column', alignItems: 'center' }}>
        <Crest team={match.home} size={flag} c={c} />
        <TextWidget
          text={match.home.code}
          style={{ fontSize: codeSize, color: c.ink, fontWeight: '700', marginTop: 8, textAlign: 'center', letterSpacing: 0.5 }}
          truncate="END"
          maxLines={1}
        />
      </FlexWidget>
      {/* Glassy score chip: gradient panel + hairline border = the focal point */}
      <FlexWidget
        style={{
          marginLeft: 10,
          marginRight: 10,
          // Fixed width keeps the score chip a steady focal block across states
          // (FlexWidget has no minWidth; width is the supported sizing prop).
          width: pre ? (big ? 78 : 64) : big ? 92 : 74,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundGradient: { from: c.scoreChipStart, to: c.scoreChipEnd, orientation: 'TOP_BOTTOM' },
          borderWidth: 1,
          borderColor: c.chipLine,
          borderRadius: rmap(14),
          paddingTop: pre ? 8 : 6,
          paddingBottom: pre ? 8 : 6,
        }}
      >
        <TextWidget
          text={score(match)}
          style={{ fontSize: scoreSize, color: scoreColor, fontWeight: '700', textAlign: 'center', letterSpacing: pre ? 0 : 1 }}
        />
      </FlexWidget>
      <FlexWidget style={{ flex: 1, flexDirection: 'column', alignItems: 'center' }}>
        <Crest team={match.away} size={flag} c={c} />
        <TextWidget
          text={match.away.code}
          style={{ fontSize: codeSize, color: c.ink, fontWeight: '700', marginTop: 8, textAlign: 'center', letterSpacing: 0.5 }}
          truncate="END"
          maxLines={1}
        />
      </FlexWidget>
    </FlexWidget>
  );
}

function FixtureList({
  data,
  rows,
  favoriteFirst,
  c,
}: {
  data: WidgetData;
  rows: number;
  favoriteFirst: boolean;
  c: WidgetTheme;
}) {
  const followed = new Set(favoriteFirst ? data.followedTeamIds : []);
  const list = orderFixtures(data.fixtures, followed).slice(0, rows);
  if (list.length === 0) return <FlexWidget style={{ height: 0 }} />;
  return (
    <FlexWidget style={{ width: 'match_parent', flexDirection: 'column', marginTop: 12 }}>
      <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 7 }}>
        <FlexWidget
          style={{
            width: 4,
            height: 12,
            borderRadius: rmap(2),
            backgroundGradient: { from: c.accentTop, to: c.accentBot, orientation: 'TOP_BOTTOM' },
            marginRight: 8,
          }}
        />
        <TextWidget
          text="UP NEXT"
          style={{ fontSize: 10, color: c.muted, fontWeight: '700', letterSpacing: 1.2 }}
        />
      </FlexWidget>
      {list.map((fx, i) => (
        <FlexWidget
          key={`${fx.home.code}-${fx.away.code}-${i}`}
          style={{
            width: 'match_parent',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 7,
            paddingBottom: 7,
            paddingLeft: 4,
            paddingRight: 4,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: c.rowLine,
          }}
        >
          <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <Flag team={fx.home} size={16} c={c} />
            <TextWidget text={`  ${fx.home.code}`} style={{ fontSize: 12, color: c.soft, fontWeight: '700' }} />
          </FlexWidget>
          <FlexWidget
            style={{
              backgroundColor: c.chip,
              borderWidth: 1,
              borderColor: c.chipLine,
              borderRadius: 8,
              paddingTop: 3,
              paddingBottom: 3,
              paddingLeft: 8,
              paddingRight: 8,
            }}
          >
            <TextWidget text={fmtTime(fx.kickoffAt)} style={{ fontSize: 11, color: c.soft, fontWeight: '700' }} />
          </FlexWidget>
          <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
            <TextWidget text={`${fx.away.code}  `} style={{ fontSize: 12, color: c.soft, fontWeight: '700' }} />
            <Flag team={fx.away} size={16} c={c} />
          </FlexWidget>
        </FlexWidget>
      ))}
    </FlexWidget>
  );
}

/**
 * Reorder upcoming fixtures so any featuring a followed team float to the top
 * (stable: ties keep chronological order). Pure + defensive — an empty followed
 * set or empty list returns the input order. Used by the `favoriteFirst` pref.
 */
function orderFixtures(fixtures: WFixture[], followed: Set<string>): WFixture[] {
  if (followed.size === 0) return fixtures;
  const isFav = (fx: WFixture) =>
    (fx.homeId != null && followed.has(fx.homeId)) ||
    (fx.awayId != null && followed.has(fx.awayId));
  // Stable partition: favored fixtures first, original relative order kept.
  return [...fixtures.filter(isFav), ...fixtures.filter((fx) => !isFav(fx))];
}

/**
 * A single rich upcoming-fixture row for the Glassify-style board:
 * [crest+code]  ·  [time over date chip]  ·  [code+crest]. When `favoriteFirst`
 * is on and a side is followed, its code is tinted with the accent so the user's
 * team pops. Every node is a Flex/Text/Image widget (never a Fragment).
 */
function UpcomingRow({
  fx,
  first,
  followed,
  c,
}: {
  fx: WFixture;
  first: boolean;
  followed: Set<string>;
  c: WidgetTheme;
}) {
  const homeFav = fx.homeId != null && followed.has(fx.homeId);
  const awayFav = fx.awayId != null && followed.has(fx.awayId);
  return (
    <FlexWidget
      style={{
        width: 'match_parent',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 4,
        paddingRight: 4,
        borderTopWidth: first ? 0 : 1,
        borderTopColor: c.rowLine,
      }}
    >
      <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
        <Flag team={fx.home} size={18} c={c} />
        <TextWidget
          text={`  ${fx.home.code}`}
          style={{ fontSize: 13, color: homeFav ? c.cyanBright : c.ink, fontWeight: '700', letterSpacing: 0.3 }}
          truncate="END"
          maxLines={1}
        />
      </FlexWidget>
      {/* Center: kickoff time stacked over the date, in a glass chip */}
      <FlexWidget
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: c.chip,
          borderWidth: 1,
          borderColor: c.chipLine,
          borderRadius: rmap(10),
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: 10,
          paddingRight: 10,
          marginLeft: 6,
          marginRight: 6,
        }}
      >
        <TextWidget text={fmtTime(fx.kickoffAt)} style={{ fontSize: 12, color: c.ink, fontWeight: '700' }} />
        <TextWidget
          text={fmtDate(fx.kickoffAt)}
          style={{ fontSize: 9, color: c.muted, fontWeight: '700', letterSpacing: 0.6, marginTop: 1 }}
        />
      </FlexWidget>
      <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
        <TextWidget
          text={`${fx.away.code}  `}
          style={{ fontSize: 13, color: awayFav ? c.cyanBright : c.ink, fontWeight: '700', letterSpacing: 0.3 }}
          truncate="END"
          maxLines={1}
        />
        <Flag team={fx.away} size={18} c={c} />
      </FlexWidget>
    </FlexWidget>
  );
}

/**
 * The Glassify-style "Upcoming fixtures" board: a titled header strip + up to
 * `rows` rich fixture rows on the glass plate. This is the whole tile body when
 * the user picks the `upcoming` layout. Returns a 0-height node when there are
 * no fixtures so the caller can fall through to the focus card / empty state.
 */
function UpcomingBoard({
  data,
  rows,
  favoriteFirst,
  c,
}: {
  data: WidgetData;
  rows: number;
  favoriteFirst: boolean;
  c: WidgetTheme;
}) {
  const followed = new Set(favoriteFirst ? data.followedTeamIds : []);
  const list = orderFixtures(data.fixtures, followed).slice(0, rows);
  if (list.length === 0) return <FlexWidget style={{ height: 0 }} />;
  return (
    <FlexWidget style={{ width: 'match_parent', flex: 1, flexDirection: 'column' }}>
      <FlexWidget style={{ width: 'match_parent', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <FlexWidget style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
          <FlexWidget
            style={{
              width: 4,
              height: 16,
              borderRadius: rmap(2),
              backgroundGradient: { from: c.accentTop, to: c.accentBot, orientation: 'TOP_BOTTOM' },
              marginRight: 9,
            }}
          />
          <TextWidget
            text="UPCOMING FIXTURES"
            style={{ fontSize: 11, color: c.soft, fontWeight: '700', letterSpacing: 0.9 }}
            truncate="END"
            maxLines={1}
          />
        </FlexWidget>
        <FlexWidget
          style={{
            backgroundColor: c.chip,
            borderWidth: 1,
            borderColor: c.chipLine,
            borderRadius: rmap(8),
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          <TextWidget text="WC 2026" style={{ fontSize: 9, color: c.muted, fontWeight: '700', letterSpacing: 0.8 }} />
        </FlexWidget>
      </FlexWidget>
      {/* Inset glass card holding the fixture rows (depth, like the hero card) */}
      <FlexWidget
        style={{
          width: 'match_parent',
          flexDirection: 'column',
          backgroundGradient: { from: c.panelStart, to: c.panelEnd, orientation: 'TOP_BOTTOM' },
          borderWidth: 1,
          borderColor: c.highlight,
          borderRadius: rmap(18),
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: 10,
          paddingRight: 10,
          marginTop: 11,
        }}
      >
        {list.map((fx, i) => (
          <UpcomingRow
            key={`${fx.home.code}-${fx.away.code}-${i}`}
            fx={fx}
            first={i === 0}
            followed={followed}
            c={c}
          />
        ))}
      </FlexWidget>
    </FlexWidget>
  );
}

/** The focus match's group table (4 rows), followed sides washed cyan. */
function StandingsTable({ data, c }: { data: WidgetData; c: WidgetTheme }) {
  const focus = data.focus;
  if (!focus || data.standings.length === 0) return <FlexWidget style={{ height: 0 }} />;
  const ids = new Set([focus.homeId, focus.awayId].filter(Boolean) as string[]);
  const groupRow = data.standings.find((s) => ids.has(s.teamId));
  const group = groupRow?.group ?? null;
  const rows: WStanding[] = (group ? data.standings.filter((s) => s.group === group) : data.standings).slice(0, 4);
  if (rows.length === 0) return <FlexWidget style={{ height: 0 }} />;
  return (
    <FlexWidget style={{ width: 'match_parent', flexDirection: 'column', marginTop: 12 }}>
      <FlexWidget
        style={{
          width: 'match_parent',
          flexDirection: 'row',
          alignItems: 'center',
          paddingLeft: 2,
          paddingRight: 6,
          marginBottom: 5,
        }}
      >
        <FlexWidget style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
          <FlexWidget
            style={{
              width: 4,
              height: 12,
              borderRadius: rmap(2),
              backgroundGradient: { from: c.accentTop, to: c.accentBot, orientation: 'TOP_BOTTOM' },
              marginRight: 8,
            }}
          />
          <TextWidget
            text={group ? `GROUP ${group.replace(/group\s*/i, '').trim().toUpperCase()}` : 'STANDINGS'}
            style={{ fontSize: 10, color: c.muted, fontWeight: '700', letterSpacing: 1.2 }}
            truncate="END"
            maxLines={1}
          />
        </FlexWidget>
        <TextWidget text="P" style={{ width: 26, fontSize: 9, color: c.inactive, fontWeight: '700', textAlign: 'center' }} />
        <TextWidget text="GD" style={{ width: 30, fontSize: 9, color: c.inactive, fontWeight: '700', textAlign: 'center' }} />
        <TextWidget
          text="PTS"
          style={{ width: 28, fontSize: 9, color: c.inactive, fontWeight: '700', textAlign: 'center' }}
        />
      </FlexWidget>
      {rows.map((s, i) => {
        const fav = ids.has(s.teamId);
        return (
          <FlexWidget
            key={s.teamId || i}
            style={{
              width: 'match_parent',
              flexDirection: 'row',
              alignItems: 'center',
              paddingTop: 5,
              paddingBottom: 5,
              paddingLeft: 6,
              paddingRight: 6,
              borderRadius: 9,
              marginBottom: 2,
              backgroundColor: fav ? c.favFill : '#00000000',
              borderWidth: 1,
              borderColor: fav ? c.favBorder : '#00000000',
            }}
          >
            <TextWidget text={`${s.rank}`} style={{ width: 16, fontSize: 12, color: c.muted, fontWeight: '700' }} />
            <Flag team={s.team} size={14} c={c} />
            <FlexWidget style={{ flex: 1 }}>
              <TextWidget
                text={`  ${s.team.code}`}
                style={{ fontSize: 13, color: c.ink, fontWeight: '600' }}
                truncate="END"
                maxLines={1}
              />
            </FlexWidget>
            <TextWidget text={`${s.played}`} style={{ width: 26, fontSize: 12, color: c.soft, textAlign: 'center' }} />
            <TextWidget
              text={s.gd > 0 ? `+${s.gd}` : `${s.gd}`}
              style={{ width: 30, fontSize: 12, color: c.muted, textAlign: 'center' }}
            />
            <TextWidget
              text={`${s.points}`}
              style={{ width: 28, fontSize: 13, color: fav ? c.cyanBright : c.ink, fontWeight: '700', textAlign: 'center' }}
            />
          </FlexWidget>
        );
      })}
    </FlexWidget>
  );
}

/**
 * The Tashkeela Match widget - a cyan-edged glass plate that adapts to the
 * Android widget size: compact score (2x2), match card + live/scorer line (4x2),
 * or match card + group standings, or the upcoming-fixtures list (4x4). Tapping
 * opens the app. The `style` prop selects one of four looks (THEMES); it
 * defaults to `gradient` and is robust to junk (normalizeWidgetStyle).
 *
 * NB: NEVER use a React Fragment (<>...</>) as a widget child. react-native-
 * android-widget's reconciler calls `element.type(props)`; a Fragment's type is
 * Symbol(react.fragment), so the call throws and the ENTIRE tile renders blank.
 * Wrap any group of sibling widgets in a FlexWidget instead.
 */
export function MatchWidget({
  data,
  width,
  height,
  style = DEFAULT_WIDGET_STYLE,
  accent = DEFAULT_WIDGET_ACCENT,
  layout = DEFAULT_WIDGET_LAYOUT,
  favoriteFirst = false,
}: {
  data: WidgetData;
  width: number;
  height: number;
  /** The selected base look. Robust to junk: normalizeWidgetStyle → gradient. */
  style?: WidgetStyle;
  /** Brand-accent recolor. Robust to junk: normalizeWidgetAccent → cyan. */
  accent?: WidgetAccent;
  /** Content layout. Robust to junk: normalizeWidgetLayout → auto. */
  layout?: WidgetLayout;
  /** Float a followed team to the top + highlight its crest. */
  favoriteFirst?: boolean;
}) {
  // Base style → theme, then overlay the chosen accent (cyan = unchanged).
  const c = applyAccent(THEMES[normalizeWidgetStyle(style)], normalizeWidgetAccent(accent));
  const lay = normalizeWidgetLayout(layout);
  const big = width >= 200;
  const tall = height >= 260;
  const radius = tall ? 22 : 20;
  const focus = data.focus;
  const showStandings = tall && focus != null && (focus.status === 'live' || focus.status === 'ft');
  // `upcoming` layout leads with the multi-match board IF there are fixtures to
  // show; otherwise it falls through to the focus card / empty state below so the
  // tile is never blank.
  const useBoard = lay === 'upcoming' && data.fixtures.length > 0;

  return (
    <FlexWidget
      clickAction="OPEN_APP"
      style={{
        height: 'match_parent',
        width: 'match_parent',
        // Top->bottom glass gradient plate. borderRadius goes through rmap()
        // (numeric — the form the JS layer normalizes into the flat corner map
        // that the native gradient branch reads; see rmap's doc block). Colors
        // come from the per-style theme `c` (see THEMES).
        backgroundGradient: { from: c.gradStart, to: c.gradEnd, orientation: 'TOP_BOTTOM' },
        borderRadius: rmap(radius),
        borderWidth: 1,
        borderColor: c.edge,
        paddingTop: 13,
        paddingBottom: 13,
        paddingLeft: 14,
        paddingRight: 14,
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {useBoard ? (
        // Glassify-style multi-match board: the whole tile is the upcoming list.
        // `rows` scales with tile height (compact shows fewer, tall shows more).
        <UpcomingBoard data={data} rows={tall ? 5 : 3} favoriteFirst={favoriteFirst} c={c} />
      ) : focus ? (
        <FlexWidget
          style={{
            width: 'match_parent',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Header match={focus} c={c} />
          {/* Inset glass card: a slightly lighter gradient panel layered on the
              plate fakes depth so the hero match reads as a premium card. */}
          <FlexWidget
            style={{
              width: 'match_parent',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundGradient: { from: c.panelStart, to: c.panelEnd, orientation: 'TOP_BOTTOM' },
              borderWidth: 1,
              borderColor: c.highlight,
              borderRadius: rmap(18),
              paddingTop: 12,
              paddingBottom: 12,
              paddingLeft: 10,
              paddingRight: 10,
              marginTop: 11,
              marginBottom: 2,
            }}
          >
            <MatchRow match={focus} big={big} c={c} />
            <FlexWidget style={{ marginTop: 11, alignItems: 'center' }}>
              <StatusLine match={focus} c={c} />
            </FlexWidget>
          </FlexWidget>
          {showStandings ? (
            <StandingsTable data={data} c={c} />
          ) : tall ? (
            <FixtureList data={data} rows={4} favoriteFirst={favoriteFirst} c={c} />
          ) : (
            <FlexWidget style={{ height: 0 }} />
          )}
        </FlexWidget>
      ) : (
        <FlexWidget style={{ width: 'match_parent', height: 'match_parent', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          {/* Empty state: a small glassy badge keeps the premium look even with
              no data (the always-rendered fallback frame). */}
          <FlexWidget
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: c.cyanSoft,
              borderWidth: 1,
              borderColor: c.cyanLine,
              borderRadius: rmap(10),
              paddingTop: 5,
              paddingBottom: 5,
              paddingLeft: 11,
              paddingRight: 12,
            }}
          >
            <FlexWidget
              style={{
                width: 5,
                height: 14,
                borderRadius: rmap(2),
                backgroundGradient: { from: c.accentTop, to: c.accentBot, orientation: 'TOP_BOTTOM' },
                marginRight: 8,
              }}
            />
            <TextWidget
              text="WORLD CUP 2026"
              style={{ fontSize: 12, color: c.ink, fontWeight: '700', letterSpacing: 1 }}
            />
          </FlexWidget>
          <TextWidget text="No match to show yet" style={{ fontSize: 11, color: c.muted, marginTop: 8 }} />
        </FlexWidget>
      )}
    </FlexWidget>
  );
}
